// ── Modelo do domínio CS (núcleo puro) ────────────────────────────
// Pós-venda Enterprise: contrato, vidas da empresa e gatilhos de saída.
// Espelha supabase/migrations/20260826120000_cs_schema.sql.
//
// Tudo aqui é função pura: nada de Supabase, DOM, fetch ou `new Date()` sem
// argumento. A data "hoje" entra por parâmetro justamente para que a
// validação da planilha seja testável sem congelar relógio.

import type { ProposalPlanLine } from '../plans/ports/IPlansRepository';
import { parseContractAddress } from '../../utils/contractAddress';

// ── Domínios (espelham os CHECKs do banco) ────────────────────────

export type ContratoStatus =
  | 'pendente' | 'dados_completos' | 'enviado' | 'assinado' | 'ativo' | 'cancelado';

export type DocumentoTipo = 'blue' | 'convenio' | 'totalpass' | 'odonto' | 'psicologo';
export type DocumentoStatus = 'pendente' | 'enviado' | 'assinado' | 'recusado';

export type VidaStatus = 'pendente' | 'enviada' | 'ativa' | 'inativa';

/**
 * Colaborador é titular. Vida+ e só adicional são dependentes e só existem
 * pendurados num colaborador — a diferença entre os dois é o plano: Vida+
 * tem o plano Vida+ do catálogo, só adicional não tem plano nenhum e só
 * leva Plano Odontológico Clin e/ou Psicólogo na Tela (ver `ehSemPlano`).
 */
export type VidaTipo = 'colaborador' | 'vida_mais' | 'so_adicional';

/** É dependente — está pendurado num colaborador, não é o titular. */
export const ehDependente = (tipo: VidaTipo): boolean => tipo !== 'colaborador';

/** Conta como vida — ocupa vaga, entra no quadro contratado e no boleto de
 *  planos. Só adicional não: não tem plano. Espelha os recortes por
 *  `tipo <> 'so_adicional'` na migração 20261001140000. */
export const contaComoVida = (tipo: VidaTipo): boolean => tipo !== 'so_adicional';

export const VIDA_TIPO_LABELS: Record<VidaTipo, string> = {
  colaborador:  'Colaborador',
  vida_mais:    'Vida+',
  so_adicional: 'Só adicional',
};

/**
 * O que a empresa é para a Bluehub. Espelha o `modelo` do Hub
 * (folha | convenio | parceira):
 *
 *   desconto_folha  a empresa desconta em folha: contrato Blue e boleto
 *                   mensal, com o Vida+ somado no mesmo boleto.
 *   convenio        conveniada: contrato de convênio, SEM boleto. As vidas
 *                   vêm do Hub, onde o colaborador contrata e paga.
 *   parceira        empresa parceira: nem contrato nem boleto. É só o passo
 *                   de agregar as vidas que vêm do Hub e gerar proposta.
 */
export type ContratoModalidade = 'desconto_folha' | 'convenio' | 'parceira';

/** Legado: o Vida+ deixou de ter forma de cobrança própria — vai no boleto
 *  sempre que a empresa é desconto em folha. A coluna continua no banco,
 *  gravada sempre `desconto_folha`. */
export type VidaMaisCobranca = 'desconto_folha' | 'cartao';

export type ImportacaoStatus = 'processando' | 'validada' | 'com_erros' | 'enviada';
export type ImportacaoFormato = 'csv' | 'xlsx';

export type CompetenciaStatus =
  | 'aberta' | 'boleto_solicitado' | 'boleto_emitido' | 'paga' | 'atrasada' | 'cancelada';

// Espelha o CHECK de cs.gatilhos depois da 20260828130000, que acrescentou
// `implantacao.liberar` e `boas_vindas.enviar`. O tipo tinha ficado para trás.
export type GatilhoTipo =
  | 'contrato.gerar' | 'vidas.enviar' | 'boleto.gerar' | 'implantacao.liberar'
  | 'acesso.enviar' | 'acesso.revogar' | 'boas_vindas.enviar';
export type GatilhoStatus = 'pendente' | 'enviando' | 'sucesso' | 'falha' | 'cancelado';
export type GatilhoTransporte = 'n8n' | 'direct';

// ── Os campos que o comercial preenche no Registrar Venda ─────────

/** Com que qualidade o signatário assina pela empresa. Domínio fechado do
 *  Hub (`representante_legal.qualificacao`): sem ela o contrato volta 422. */
export type RepresentanteQualificacao = 'socio' | 'administrador' | 'procurador';

export const QUALIFICACAO_LABELS: Record<RepresentanteQualificacao, string> = {
  socio:         'Sócio',
  administrador: 'Administrador',
  procurador:    'Procurador',
};

/** Sem escolha, quem assina é o administrador: é o caso comum, e o Hub não
 *  aceita "não declarada". */
export const QUALIFICACAO_PADRAO: RepresentanteQualificacao = 'administrador';

export const qualificacaoOuPadrao = (valor: unknown): RepresentanteQualificacao =>
  typeof valor === 'string' && valor in QUALIFICACAO_LABELS
    ? (valor as RepresentanteQualificacao) : QUALIFICACAO_PADRAO;

export interface ContratoDados {
  razaoSocial: string;
  cnpj: string;
  endereco: string;
  email: string;
  representanteNome: string;
  representanteCpf: string;
  emailAssinatura: string;
  /** O "Cargo" da tela. Vai ao Hub como `qualificacao` e como `cargo`. */
  representanteQualificacao: RepresentanteQualificacao | '';
  /** Declaração jurídica de que o signatário tem poderes de representação
   *  (contrato social, procuração). Só vale marcada de propósito. */
  representanteDeclaraPoderes: boolean;
}

/** Os campos de texto livre — os que viram `<Input>` no formulário. */
export type ContratoCampoTexto = Exclude<keyof ContratoDados, 'representanteQualificacao' | 'representanteDeclaraPoderes'>;

export const emptyContratoDados = (): ContratoDados => ({
  razaoSocial: '', cnpj: '', endereco: '', email: '',
  representanteNome: '', representanteCpf: '', emailAssinatura: '',
  representanteQualificacao: QUALIFICACAO_PADRAO, representanteDeclaraPoderes: false,
});

export const CONTRATO_CAMPO_LABELS: Record<keyof ContratoDados, string> = {
  razaoSocial:       'Razão social',
  cnpj:              'CNPJ',
  endereco:          'Endereço',
  email:             'E-mail de cobrança',
  representanteNome: 'Representante legal',
  representanteCpf:  'CPF do representante',
  emailAssinatura:   'E-mail para assinatura',
  representanteQualificacao:   'Cargo',
  representanteDeclaraPoderes: 'Declaração de poderes do signatário',
};

// ── Negociação com a empresa ──────────────────────────────────────
// Uma decisão só: o que a empresa é. Dela saem o contrato e o boleto.

export const MODALIDADE_LABELS: Record<ContratoModalidade, string> = {
  desconto_folha: 'Desconto em folha',
  convenio:       'Conveniada',
  parceira:       'Empresa parceira',
};

export const MODALIDADE_AJUDA: Record<ContratoModalidade, string> = {
  desconto_folha:
    'A empresa desconta em folha e paga a Bluehub. Gera contrato e boleto '
    + 'mensal pelas vidas, com o Vida+ somado.',
  convenio:
    'Gera contrato, mas não gera boleto: as vidas vêm do Hub, onde o '
    + 'colaborador contrata e paga.',
  parceira:
    'Não gera contrato nem boleto. Serve para agregar as vidas que vêm do Hub '
    + 'e gerar proposta.',
};

export const MODALIDADE_PADRAO: ContratoModalidade = 'desconto_folha';
export const VIDA_MAIS_COBRANCA_PADRAO: VidaMaisCobranca = 'desconto_folha';

/** Existe boleto para esta empresa? Só no desconto em folha, e aí o Vida+
 *  vai junto. Espelha `cs.empresa_recebe_boleto` — mexeu aqui, mexa lá. */
export const empresaRecebeBoleto = (modalidade: ContratoModalidade): boolean =>
  modalidade === 'desconto_folha';

/** Existe contrato para esta empresa? A parceira não assina nada.
 *  Espelha `recusaPorModalidade` em server/cs-automation.js. */
export const empresaGeraContrato = (modalidade: ContratoModalidade): boolean =>
  modalidade !== 'parceira';

/** Por que não há boleto — a frase que a tela mostra no lugar do botão. */
export const motivoSemBoleto = (modalidade: ContratoModalidade): string | null => {
  if (empresaRecebeBoleto(modalidade)) return null;
  return modalidade === 'parceira'
    ? 'Empresa parceira: não há boleto, as vidas vêm do Hub.'
    : 'Conveniada: a empresa não recebe boleto, as vidas vêm do Hub.';
};

/** Por que não há contrato — idem, para o botão de gerar contrato. */
export const motivoSemContrato = (modalidade: ContratoModalidade): string | null =>
  empresaGeraContrato(modalidade)
    ? null
    : 'Empresa parceira: não há contrato a assinar.';

/**
 * Por que o botão não dispara quando a rota escolhida no Super Admin é Hub e o
 * servidor ainda não tem o Hub ligado (BLUEHUB_API_BASE + chave de escrita).
 * Espelha `recusaPorRota` em server/cs-automation.js: o recorte de verdade é o
 * do servidor, este só evita o clique que ia voltar 409 — e, no boleto, a
 * competência que ficaria criada sem nunca ser enviada.
 *
 * `hubLigado` desconhecido (leitura em curso ou falhou) conta como desligado:
 * travar a mais só custa um clique; destravar a mais cria a competência e
 * leva um 409 do servidor.
 */
export const motivoRotaHub = (
  oQue: 'contrato' | 'cobranca',
  via: 'n8n' | 'hub' | undefined,
  hubLigado = false,
): string | null =>
  via === 'hub' && !hubLigado
    ? `${oQue === 'contrato' ? 'O contrato' : 'A cobrança'} desta empresa sai pelo Hub, e a integração ainda não está ligada.`
    : null;

/**
 * Os envios que o Super Admin pode desativar por empresa: os que têm botão.
 * `vidas.enviar`, `acesso.revogar` e `implantacao.liberar` não são disparados
 * por tela nenhuma hoje, e oferecê-los faria parecer que "Liberar implantação"
 * (que não é gatilho) ficou travado.
 */
export const TIPOS_BLOQUEAVEIS: GatilhoTipo[] = [
  'contrato.gerar', 'boleto.gerar', 'acesso.enviar', 'boas_vindas.enviar',
];

/**
 * Por que o botão não dispara quando o envio foi desativado no Super Admin.
 * Espelha `recusaPorBloqueio` em server/cs-automation.js.
 */
export const motivoEnvioBloqueado = (
  tipo: GatilhoTipo,
  bloqueios: { tipo: GatilhoTipo; motivo: string }[] | undefined,
): string | null => {
  const b = (bloqueios || []).find(x => x.tipo === tipo);
  return b ? `Envio desativado para esta empresa no Super Admin: ${b.motivo}` : null;
};

// ── Normalização ──────────────────────────────────────────────────

export const onlyDigits = (value: unknown): string =>
  String(value ?? '').replace(/\D/g, '');

// Sem acento e sem caixa. Usado para casar nome de plano e cabeçalho de
// coluna — o texto vem de planilha digitada à mão, "Odontológico" e
// "odontologico" precisam colidir.
// A classe é escrita com escapes (̀-ͯ = marcas de acento soltas
// pelo NFD) e não com os caracteres literais: combinantes soltos no código
// somem em copy/paste e em normalização de editor, e o bug resultante é
// invisível na revisão.
const ACENTOS_NFD = /[\u0300-\u036f]/g;

export const normalizeText = (value: unknown): string =>
  String(value ?? '')
    .normalize('NFD')
    .replace(ACENTOS_NFD, '')
    .trim()
    .toLowerCase();

/**
 * Chave de comparação de nome de plano. Além do `normalizeText`, colapsa
 * espaço repetido (a célula volta do cliente com "Blue  Protect") e, com
 * `semPrefixo`, tira o "Plano " da frente — é o que faz o "Alicerce" do
 * dropdown da planilha achar o "Plano Alicerce" do catálogo.
 */
export const chavePlano = (nome: unknown, semPrefixo = false): string => {
  const base = normalizeText(nome).replace(/\s+/g, ' ');
  return semPrefixo ? base.replace(/^plano\s+/, '') : base;
};

/** É o plano Vida+? "Plano VIDA+", "Vida+", "Vida +" — o nome vem de
 *  catálogo e de planilha, e as grafias já divergiram. Espelha
 *  `cs.eh_vida_mais` na migração 20260910120000. */
export const ehPlanoVidaMais = (nome: unknown): boolean =>
  chavePlano(nome, true).replace(/\s+/g, '') === 'vida+';

/** O texto que a planilha modelo usa na célula de Plano do dependente que
 *  não é Vida+. A tela grava o mesmo texto quando cria um "só adicional"
 *  pela mão, para o dado ficar igual venha de onde vier. */
export const PLANO_SEM_PLANO_TEXTO = 'Nenhum Plano contratado';

/** "Nenhum Plano contratado" — a opção do dropdown da aba Dependentes para
 *  quem só leva adicional (odonto, psicólogo). Não é plano, e sem plano a
 *  pessoa não é vida: não ocupa vaga no Hub nem entra no boleto de planos.
 *  No dependente, é o que decide Vida+ x só adicional em `validarPlanilha`. */
export const ehSemPlano = (nome: unknown): boolean =>
  /^nenhum\b/.test(chavePlano(nome, true));

// ── CPF / CNPJ ────────────────────────────────────────────────────

export const isValidCpf = (raw: unknown): boolean => {
  const cpf = onlyDigits(raw);
  if (cpf.length !== 11) return false;
  // 111.111.111-11 e afins passam no dígito verificador e não são CPF.
  if (/^(\d)\1{10}$/.test(cpf)) return false;

  const digito = (len: number): number => {
    let soma = 0;
    for (let i = 0; i < len; i += 1) soma += Number(cpf[i]) * (len + 1 - i);
    const resto = (soma * 10) % 11;
    return resto === 10 ? 0 : resto;
  };

  return digito(9) === Number(cpf[9]) && digito(10) === Number(cpf[10]);
};

export const isValidCnpj = (raw: unknown): boolean => {
  const cnpj = onlyDigits(raw);
  if (cnpj.length !== 14) return false;
  if (/^(\d)\1{13}$/.test(cnpj)) return false;

  const digito = (len: number): number => {
    let peso = len - 7;
    let soma = 0;
    for (let i = 0; i < len; i += 1) {
      soma += Number(cnpj[i]) * peso;
      peso -= 1;
      if (peso < 2) peso = 9;
    }
    const resto = soma % 11;
    return resto < 2 ? 0 : 11 - resto;
  };

  return digito(12) === Number(cnpj[12]) && digito(13) === Number(cnpj[13]);
};

export const formatCpf = (raw: unknown): string => {
  const d = onlyDigits(raw);
  if (d.length !== 11) return String(raw ?? '');
  return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
};

export const formatCnpj = (raw: unknown): string => {
  const d = onlyDigits(raw);
  if (d.length !== 14) return String(raw ?? '');
  return `${d.slice(0, 2)}.${d.slice(2, 5)}.${d.slice(5, 8)}/${d.slice(8, 12)}-${d.slice(12)}`;
};

// Exibição padrão na tela do CS: esconde início e fim, que é o que
// permite reconstruir o número. Revelar é ação explícita e logada.
export const maskCpf = (raw: unknown): string => {
  const d = onlyDigits(raw);
  if (d.length !== 11) return '—';
  return `***.${d.slice(3, 6)}.${d.slice(6, 9)}-**`;
};

// ── Máscaras de digitação ─────────────────────────────────────────
// Formatam o valor PARCIAL, enquanto a pessoa digita — ao contrário de
// `formatCpf`, que devolve o texto cru quando o número ainda não está
// completo. Quem preenche a ficha de uma vida digita só os números; sem
// isto o campo fica com "07051999" na tela e o erro só aparece ao salvar.
//
// Nenhuma delas fecha o separador antes do próximo dígito existir: com
// "07/" na tela, apagar um caractere devolveria o separador na mesma hora
// e o backspace nunca andaria.

export const mascararCpf = (raw: unknown): string => {
  const d = onlyDigits(raw).slice(0, 11);
  const inicio = [d.slice(0, 3), d.slice(3, 6), d.slice(6, 9)].filter(Boolean).join('.');
  return d.length > 9 ? `${inicio}-${d.slice(9)}` : inicio;
};

/** dd/mm/aaaa — a grafia que a planilha modelo pede e que o formulário usa. */
export const mascararDataBr = (raw: unknown): string => {
  const d = onlyDigits(raw).slice(0, 8);
  return [d.slice(0, 2), d.slice(2, 4), d.slice(4, 8)].filter(Boolean).join('/');
};

/** (11) 90000-0000 e (11) 3000-0000. Um +55 colado na frente sai fora: o
 *  telefone é guardado como o RH digita, e o DDI duplicaria o DDD. */
export const mascararTelefone = (raw: unknown): string => {
  const cru = onlyDigits(raw);
  const d = (cru.length > 11 && cru.startsWith('55') ? cru.slice(2) : cru).slice(0, 11);
  if (!d) return '';
  if (d.length <= 2) return `(${d}`;

  const resto = d.slice(2);
  if (resto.length <= 4) return `(${d.slice(0, 2)}) ${resto}`;
  return `(${d.slice(0, 2)}) ${resto.slice(0, resto.length - 4)}-${resto.slice(-4)}`;
};

/** ISO do banco → dd/mm/aaaa. O caminho de volta é `parseDataNascimento`. */
export const formatarDataBr = (iso: unknown): string => {
  const [a, m, d] = String(iso ?? '').split('-');
  return a && m && d ? `${d}/${m}/${a}` : String(iso ?? '');
};

// ── E-mail ────────────────────────────────────────────────────────
// Deliberadamente frouxo: validar e-mail por regex estrita rejeita
// endereço válido, e quem diz a verdade é o convite que o hub manda.
// Aqui só barramos o que é obviamente digitação errada.
export const isValidEmail = (raw: unknown): boolean =>
  /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(raw ?? '').trim());

// ── Data de nascimento ────────────────────────────────────────────

/** Idade mínima aceita. 14 é o piso constitucional (aprendiz); acima disso
 *  é política de RH da empresa, não nossa — rejeitar aqui bloquearia uma
 *  venda por causa de uma regra que não é do CRM. */
export const IDADE_MINIMA = 14;
/** Limite do plano: a vida precisa ter MENOS de 70 anos na importação. */
export const IDADE_MAXIMA = 70;

// A faixa é conferida em MESES, não em anos inteiros. A regra da operadora é
// escrita como "até 69 anos e 11 meses", e em anos inteiros essa fronteira
// simplesmente não existe no código: 69 anos e 1 dia e 69 anos e 11 meses viram
// o mesmo 69. Contar em meses deixa o limite dizer o que a regra diz.
export const IDADE_MINIMA_MESES = IDADE_MINIMA * 12;        // 14 anos
export const IDADE_MAXIMA_MESES = (IDADE_MAXIMA * 12) - 1;  // 69 anos e 11 meses
/** Acima disto não é regra de plano, é data digitada errada. */
const IDADE_IMPLAUSIVEL = 120;

/** Aceita `dd/mm/aaaa` (o que a planilha modelo pede) e `aaaa-mm-dd`.
 *  Devolve ISO `aaaa-mm-dd` ou null. Rejeita 31/02 por round-trip. */
export const parseDataNascimento = (raw: unknown): string | null => {
  const texto = String(raw ?? '').trim();
  if (!texto) return null;

  const br = /^(\d{1,2})\/(\d{1,2})\/(\d{4})$/.exec(texto);
  const iso = /^(\d{4})-(\d{1,2})-(\d{1,2})$/.exec(texto);

  let ano: number; let mes: number; let dia: number;
  if (br) { dia = Number(br[1]); mes = Number(br[2]); ano = Number(br[3]); }
  else if (iso) { ano = Number(iso[1]); mes = Number(iso[2]); dia = Number(iso[3]); }
  else return null;

  const d = new Date(Date.UTC(ano, mes - 1, dia));
  // 31/02 vira 03/03 no construtor; o round-trip pega isso.
  if (d.getUTCFullYear() !== ano || d.getUTCMonth() !== mes - 1 || d.getUTCDate() !== dia) {
    return null;
  }
  return `${ano}-${String(mes).padStart(2, '0')}-${String(dia).padStart(2, '0')}`;
};

/** Idade completa em anos na data de referência. */
export const idadeEm = (nascimentoIso: string, hoje: Date): number => {
  const [a, m, d] = nascimentoIso.split('-').map(Number);
  let idade = hoje.getUTCFullYear() - a;
  const mesAtual = hoje.getUTCMonth() + 1;
  const diaAtual = hoje.getUTCDate();
  if (mesAtual < m || (mesAtual === m && diaAtual < d)) idade -= 1;
  return idade;
};

/** Idade completa em MESES na data de referência — é nessa unidade que a
 *  faixa etária do plano é escrita ("de 14 anos a 69 anos e 11 meses"). */
export const idadeEmMesesEm = (nascimentoIso: string, hoje: Date): number => {
  const [a, m, d] = nascimentoIso.split('-').map(Number);
  const meses = (hoje.getUTCFullYear() - a) * 12 + (hoje.getUTCMonth() + 1 - m);
  // Mês ainda não fechou: quem nasceu dia 20 só completa o mês no dia 20.
  return hoje.getUTCDate() < d ? meses - 1 : meses;
};

/** "69 anos e 11 meses" / "40 anos" — o jeito que o erro precisa falar. */
const idadePorExtenso = (meses: number): string => {
  const anos = Math.floor(meses / 12);
  const resto = meses % 12;
  const parteAnos = `${anos} ${anos === 1 ? 'ano' : 'anos'}`;
  if (!resto) return parteAnos;
  return `${parteAnos} e ${resto} ${resto === 1 ? 'mês' : 'meses'}`;
};

// ── Quais contratos a venda exige ─────────────────────────────────
// Sai das linhas da proposta, não de escolha na tela. `blue` é o contrato
// geral e existe sempre; os demais só quando o adicional foi vendido.
//
// O casamento é por palavra-chave e não por nome exato de propósito: os
// nomes do catálogo vieram de planilha ("Psicologo na tela (Zenklub)",
// "Plano odontologico") e já mudaram de grafia uma vez.
//
// Plano VIDA+ é adicional mas NÃO gera documento próprio: é dependente de
// titular, coberto pelo contrato Blue.
const ADDON_DOCUMENTOS: Array<{ match: RegExp; tipo: DocumentoTipo }> = [
  { match: /total\s*pass/,     tipo: 'totalpass' },
  { match: /odonto/,           tipo: 'odonto' },
  { match: /psicolog|zenklub/, tipo: 'psicologo' },
];

/**
 * `blue` e `convenio` são o MESMO lugar na lista — o contrato geral da
 * empresa — e são peças jurídicas diferentes: no convênio a empresa não
 * assume a mensalidade, só abre a porta. Por isso a modalidade escolhe qual
 * dos dois nasce, em vez de gerar os dois e deixar alguém decidir depois.
 * Empresa parceira não assina nada: nem o geral, nem anexo.
 */
export const documentosNecessarios = (
  linhas: ProposalPlanLine[] | undefined,
  modalidade: ContratoModalidade = MODALIDADE_PADRAO,
): DocumentoTipo[] => {
  if (!empresaGeraContrato(modalidade)) return [];
  const tipos = new Set<DocumentoTipo>([modalidade === 'convenio' ? 'convenio' : 'blue']);
  (linhas || [])
    .filter(l => l && l.kind === 'addon' && l.quantity > 0)
    .forEach((l) => {
      const nome = normalizeText(l.name);
      const achado = ADDON_DOCUMENTOS.find(a => a.match.test(nome));
      if (achado) tipos.add(achado.tipo);
    });
  return Array.from(tipos);
};

// ── Quem recebe acesso à plataforma ───────────────────────────────
// `acesso.enviar` cadastra a vida na MemberKit, libera a turma e manda o
// e-mail do Brevo com o código OINC (ver n8n/bluehub/README.md). Isso é a
// plataforma de educação financeira — e o Blue Light não a inclui: é o plano
// de entrada, só saúde e proteção. Liberar acesso para ele daria à vida um
// benefício que a empresa não comprou.
//
// Casa por palavra-chave, e não por nome exato, pelo mesmo motivo de
// ADDON_DOCUMENTOS: o nome vem de planilha digitada à mão e já mudou de
// grafia uma vez. `plano_nome_raw` é justamente o texto cru da planilha.
const PLANOS_SEM_ACESSO: RegExp[] = [
  /blue\s*light/,
];

/** A vida deste plano recebe acesso à plataforma? Recebe por padrão — a lista
 *  é de exceções, então plano novo no catálogo entra liberado. Plano em branco
 *  também libera: quem barra planilha sem plano é `validarPlanilha`, e um
 *  campo vazio não é motivo para negar acesso silenciosamente.
 *
 *  ATENÇÃO: quem de fato recorta o disparo é `server/cs-automation.js` —
 *  o front usa isto só para explicar e desabilitar o botão. Mexeu aqui,
 *  mexa lá (e nos dois testes que travam a regra). */
export const planoLiberaAcesso = (planoNomeRaw: string | null | undefined): boolean => {
  const nome = normalizeText(String(planoNomeRaw ?? ''));
  if (!nome) return true;
  return !PLANOS_SEM_ACESSO.some(re => re.test(nome));
};

/** Preço por vida do Total Pass legado, espelhando o checkbox da proposta. */
export const TOTAL_PASS_UNIT = 6;

export interface TotalPassLegado {
  totalPassEnabled?: boolean | null;
  totalPassQty?: number | null;
}

/**
 * O Total Pass legado não vive em `plans`: é um checkbox à parte da proposta,
 * de antes do catálogo v2. Mas para o contrato ele é adicional como qualquer
 * outro — soma no valor, NÃO conta como vida e gera documento próprio.
 *
 * Vira linha sintética aqui para que todo mundo que lê `ProposalPlanLine[]`
 * enxergue a mesma venda. Sem isto, `documentosNecessarios` não gerava o
 * contrato Total Pass de uma venda que tinha Total Pass.
 *
 * No-op quando o catálogo já traz o adicional — senão apareceria duas vezes
 * assim que o workspace migrar e o checkbox sumir.
 */
export const comTotalPassLegado = (
  linhas: ProposalPlanLine[] | undefined,
  proposta?: TotalPassLegado | null,
): ProposalPlanLine[] => {
  const base = linhas || [];
  const quantidade = Math.max(0, Math.floor(Number(proposta?.totalPassQty) || 0));
  if (!proposta?.totalPassEnabled || quantidade <= 0) return base;
  if (documentosNecessarios(base).includes('totalpass')) return base;

  return [...base, {
    planId: 'total-pass-legado',
    name: 'Total Pass',
    quantity: quantidade,
    unitValue: TOTAL_PASS_UNIT,
    totalValue: quantidade * TOTAL_PASS_UNIT,
    kind: 'addon',
  }];
};

export const DOCUMENTO_LABELS: Record<DocumentoTipo, string> = {
  blue:      'Contrato Blue',
  convenio:  'Contrato de convênio',
  totalpass: 'Total Pass',
  odonto:    'Plano odontológico',
  psicologo: 'Psicólogo na tela',
};

// ── Completude do contrato ────────────────────────────────────────

/** Quem assina o contrato. A empresa parceira não assina nada, então esses
 *  campos nem aparecem para ela — nem contam como faltando. */
export const CAMPOS_DE_ASSINATURA: Array<keyof ContratoDados> = [
  'representanteNome', 'representanteCpf', 'emailAssinatura',
  'representanteQualificacao', 'representanteDeclaraPoderes',
];

/** Os campos obrigatórios desta empresa. Espelha `submit_contrato_form`. */
export const camposDoContrato = (
  modalidade: ContratoModalidade = MODALIDADE_PADRAO,
): Array<keyof ContratoDados> =>
  (Object.keys(CONTRATO_CAMPO_LABELS) as Array<keyof ContratoDados>)
    .filter(campo => empresaGeraContrato(modalidade) || !CAMPOS_DE_ASSINATURA.includes(campo));

/** O endereço é uma string só, mas o contrato precisa de cada pedaço: "Rua X"
 *  sem UF e CEP não é endereço. Só o complemento é opcional. */
export const enderecoContratoCompleto = (endereco: string | null | undefined): boolean => {
  const e = parseContractAddress(String(endereco ?? ''));
  return !!(e.street && e.number && e.neighborhood && e.city)
    && /^[A-Z]{2}$/.test(e.state)
    && onlyDigits(e.zipCode).length === 8;
};

export const contratoCamposFaltando = (
  dados: ContratoDados,
  modalidade?: ContratoModalidade,
): Array<keyof ContratoDados> =>
  camposDoContrato(modalidade).filter(campo => {
    const valor = dados[campo];
    // A declaração de poderes falta enquanto não for marcada: o Hub recusa o
    // contrato sem ela, como recusa sem CPF.
    if (typeof valor === 'boolean') return !valor;
    if (!String(valor ?? '').trim()) return true;
    // Preenchido mas errado também barra o envio: o contrato sairia com um
    // CNPJ ou CPF que o Hub recusa.
    if (campo === 'endereco') return !enderecoContratoCompleto(dados.endereco);
    if (campo === 'cnpj') return !isValidCnpj(dados.cnpj);
    if (campo === 'representanteCpf') return !isValidCpf(dados.representanteCpf);
    return false;
  });

/** `dados_completos` quer dizer "pronto para enviar": nada em branco, endereço
 *  inteiro e CNPJ/CPF com dígito válido.
 *  A venda nunca é bloqueada por isto (decisão de produto); o status só
 *  determina se o gatilho de contrato pode ser disparado. */
export const contratoStatusInicial = (dados: ContratoDados, modalidade?: ContratoModalidade): ContratoStatus =>
  contratoCamposFaltando(dados, modalidade).length === 0 ? 'dados_completos' : 'pendente';

/** Para quem lê o contrato de fora do fluxo que o preenche — a área de
 *  Implantação não sabe o que `dados_completos` quer dizer. */
export const CONTRATO_STATUS_LABELS: Record<ContratoStatus, string> = {
  pendente:        'Dados pendentes',
  dados_completos: 'Dados completos',
  enviado:         'Enviado para assinatura',
  assinado:        'Assinado',
  ativo:           'Ativo',
  cancelado:       'Cancelado',
};

// ── Status de envio do contrato e do boleto ───────────────────────
// Derivados de cs.contrato_documentos e cs.competencias, que é onde o
// executor do Hub grava o desfecho dele (`signed` → assinado, o resto →
// enviado; `charged` → boleto_emitido). Com a rota n8n, quem grava é o
// disparo e a marcação manual. Não há leitura de assinatura no Hub: o
// receptor de eventos dele ainda não existe.

export type StatusEnvioContrato = 'envio_pendente' | 'enviado' | 'assinado' | 'recusado';

export const STATUS_ENVIO_CONTRATO_LABELS: Record<StatusEnvioContrato, string> = {
  envio_pendente: 'Envio pendente',
  enviado:        'Enviado',
  assinado:       'Assinado',
  recusado:       'Recusado',
};

/** Conta só o que saiu. Pelo Hub sai um envelope, registrado no documento
 *  principal; os anexos ficam `pendente` para sempre, e exigir todos
 *  assinados deixaria esse contrato eternamente "Enviado". */
export const statusEnvioContrato = (documentos: Array<{ status: DocumentoStatus }>): StatusEnvioContrato => {
  const sairam = documentos.filter(d => d.status !== 'pendente');
  if (sairam.length === 0) return 'envio_pendente';
  if (sairam.some(d => d.status === 'recusado')) return 'recusado';
  if (sairam.every(d => d.status === 'assinado')) return 'assinado';
  return 'enviado';
};

/** Depois de enviado o contrato não é GERADO de novo pela tela — isso
 *  mandaria um segundo envelope por engano. Recusado volta a liberar.
 *  Enviado ainda pode ser REENVIADO (`podeReenviarContrato`), de propósito. */
export const motivoContratoJaEnviado = (status: StatusEnvioContrato): string | null => {
  if (status === 'enviado') return 'Contrato já enviado para assinatura.';
  if (status === 'assinado') return 'Contrato já assinado.';
  return null;
};

/** Enviado e ainda não assinado: dá para ajustar os dados e reenviar.
 *  Espelha `recusaPorEnvio` em server/cs-automation.js. */
export const podeReenviarContrato = (status: StatusEnvioContrato): boolean => status === 'enviado';

export type StatusBoleto = 'nao_gerado' | 'solicitado' | 'emitido' | 'pago' | 'atrasado';

export const STATUS_BOLETO_LABELS: Record<StatusBoleto, string> = {
  nao_gerado: 'Não gerado',
  solicitado: 'Solicitado',
  emitido:    'Emitido',
  pago:       'Pago',
  atrasado:   'Atrasado',
};

const BOLETO_DA_COMPETENCIA: Record<CompetenciaStatus, StatusBoleto | null> = {
  aberta:            'nao_gerado',
  boleto_solicitado: 'solicitado',
  boleto_emitido:    'emitido',
  paga:              'pago',
  atrasada:          'atrasado',
  cancelada:         null,
};

/** Status da competência mais recente que não foi cancelada. `competencia`
 *  é `AAAA-MM-DD`, então a ordem de texto é a do calendário. */
export const competenciaAtualDoBoleto = <T extends { competencia: string; status: CompetenciaStatus }>(
  competencias: T[],
): T | null =>
  [...competencias]
    .filter(c => c.status !== 'cancelada')
    .sort((a, b) => b.competencia.localeCompare(a.competencia))[0] || null;

export const statusBoleto = (competencias: Array<{ competencia: string; status: CompetenciaStatus }>): StatusBoleto => {
  const atual = competenciaAtualDoBoleto(competencias);
  return (atual && BOLETO_DA_COMPETENCIA[atual.status]) || 'nao_gerado';
};

/** Pagamento em dia: há cobrança e todas as não canceladas estão pagas.
 *  Cancelada não conta — não há como pagá-la, e ela travaria a liberação. */
export const pagamentoQuitado = (competencias: Array<{ status: CompetenciaStatus }>): boolean => {
  const validas = competencias.filter(c => c.status !== 'cancelada');
  return validas.length > 0 && validas.every(c => c.status === 'paga');
};

/**
 * O que ainda segura a liberação da implantação, ou null. O que não existe
 * para o tipo de empresa não conta: parceira libera direto, conveniada só
 * depende do contrato, desconto em folha de contrato e pagamento.
 */
export const motivoBloqueioLiberacao = (
  modalidade: ContratoModalidade,
  contrato: StatusEnvioContrato,
  pagamentoEmDia: boolean,
): string | null => {
  const falta = [
    empresaGeraContrato(modalidade) && contrato !== 'assinado' && 'a assinatura do contrato',
    empresaRecebeBoleto(modalidade) && !pagamentoEmDia && 'o pagamento',
  ].filter(Boolean);
  return falta.length ? `Conclua ${falta.join(' e ')} primeiro` : null;
};

// ── Planilha de vidas ─────────────────────────────────────────────

/** Uma linha crua, já mapeada por coluna pelo parser (infra). */
export interface PlanilhaLinha {
  /** Número da linha na planilha original — é o que aparece no erro. */
  numero: number;
  /** Aba de origem. As duas abas do modelo numeram a partir da linha 13, então
   *  sem isto "linha 13" apontaria para dois lugares diferentes. */
  aba?: string;
  tipo: VidaTipo;
  nome: string;
  cpf: string;
  dataNascimento: string;
  email: string;
  telefone: string;
  plano: string;
  /** Só na aba Vida+: o colaborador em que o dependente se pendura. */
  titularNome?: string;
  titularCpf?: string;
  titularTelefone?: string;
  /** Marcações Sim/Não da aba Colaborador, como vieram da célula. */
  odonto?: string;
  psicologo?: string;
  /** Exigido pelo Plano Odontológico Clin: obrigatório quando odonto = Sim. */
  nomeMae?: string;
}

export interface PlanilhaErro {
  linha: number;
  aba?: string;
  campo?: keyof PlanilhaLinha;
  motivo: string;
}

/** "Vida+ · linha 13" — o endereço completo do erro na planilha. */
export const enderecoDoErro = (erro: PlanilhaErro): string =>
  erro.aba ? `${erro.aba} · linha ${erro.linha}` : `linha ${erro.linha}`;

// ── Correção na tela ──────────────────────────────────────────────
// O preview deixa corrigir a linha com erro ali mesmo, sem voltar ao Excel.
// A linha é achada por aba + número: as duas abas do modelo numeram a partir
// da mesma linha, então o número sozinho apontaria para duas pessoas.

/** Chave de uma linha da planilha — a mesma de `chaveDoErro`. */
export const chaveDaLinha = (linha: Pick<PlanilhaLinha, 'aba' | 'numero'>): string =>
  `${linha.aba ?? ''}#${linha.numero}`;

/** Chave da linha a que o erro se refere. */
export const chaveDoErro = (erro: Pick<PlanilhaErro, 'aba' | 'linha'>): string =>
  `${erro.aba ?? ''}#${erro.linha}`;

/** Troca os campos de uma linha. Aba e número não mudam: são o endereço dela
 *  no arquivo, e é por eles que o erro e o registro da importação a acham. */
export const corrigirLinhaDaPlanilha = (
  linhas: PlanilhaLinha[],
  chave: string,
  patch: Partial<Omit<PlanilhaLinha, 'aba' | 'numero'>>,
): PlanilhaLinha[] =>
  linhas.map(l => (chaveDaLinha(l) === chave ? { ...l, ...patch, aba: l.aba, numero: l.numero } : l));

/** Tira a linha da importação — ela não vira vida nem erro registrado. */
export const descartarLinhaDaPlanilha = (linhas: PlanilhaLinha[], chave: string): PlanilhaLinha[] =>
  linhas.filter(l => chaveDaLinha(l) !== chave);

export interface VidaImportada {
  linha: number;
  /** Aba de origem — com `linha`, é o endereço da vida na planilha
   *  (`chaveDaLinha({ aba, numero: linha })`). */
  aba?: string;
  tipo: VidaTipo;
  nome: string;
  cpf: string;                 // só dígitos
  dataNascimento: string;      // ISO
  email: string;               // lower
  telefone: string;
  planoId: string | null;
  planoNomeRaw: string;
  /** CPF (dígitos) do colaborador titular. Só em dependente
   *  (`vida_mais` ou `so_adicional`). */
  titularCpf: string | null;
  odonto: boolean;
  psicologo: boolean;
  /** Só com odonto marcado; sem odonto é sempre null (não guarda à toa). */
  nomeMae: string | null;
}

export interface PlanoDoCatalogo {
  id: string;
  name: string;
}

export interface ValidarPlanilhaOptions {
  /** Catálogo ativo do workspace, só `kind = 'plan'`. */
  planos: PlanoDoCatalogo[];
  /** Já cadastrados nesta empresa — o índice único do banco recusaria depois. */
  cpfsExistentes?: string[];
  emailsExistentes?: string[];
  /** CPFs dos COLABORADORES já cadastrados. Um Vida+ pode se pendurar num
   *  titular que entrou numa importação anterior e não vem nesta planilha. */
  cpfsColaboradoresExistentes?: string[];
  /** Data de referência para a checagem de idade. */
  hoje: Date;
}

export interface PlanilhaValidada {
  vidas: VidaImportada[];
  erros: PlanilhaErro[];
  totalLinhas: number;
  totalValidas: number;
  totalErros: number;
  /** Linhas de exemplo e notas do modelo, descartadas em silêncio. */
  ignoradas: number;
}

/** Campos preenchidos na aba Resumo, preservados como retrato da negociação. */
export interface NegociacaoPlanilha {
  resumo: Array<{ item: string; valores: string[] }>;
}

// As linhas de exemplo do Modelo-Planilha BlueHub, nas duas abas. Só
// descartamos uma linha quando CPF **e** e-mail batem com o exemplo — casar
// por um campo só apagaria dado real de quem por acaso reusou um deles.
const LINHAS_EXEMPLO: Array<{ cpf: string; email: string }> = [
  { cpf: '00011122233', email: 'colaborador1@empresa.com.br' },
  { cpf: '11122233344', email: 'colaborador2@empresa.com.br' },
  { cpf: '22233344455', email: 'vida1@empresa.com.br' },
  { cpf: '33344455566', email: 'vida2@empresa.com.br' },
];

const isLinhaExemplo = (cpf: string, email: string): boolean =>
  LINHAS_EXEMPLO.some(ex => ex.cpf === cpf && ex.email === email.toLowerCase());

const isLinhaVazia = (linha: PlanilhaLinha): boolean =>
  !`${linha.nome}${linha.cpf}${linha.dataNascimento}${linha.email}${linha.telefone}`
    .concat(`${linha.plano}${linha.titularNome || ''}${linha.titularCpf || ''}`).trim();

/** Célula de marcação da planilha ("Sim"/"Não" no dropdown do modelo). */
export const marcadoNaPlanilha = (valor: unknown): boolean => {
  const t = normalizeText(valor);
  return t === 'sim' || t === 's' || t === 'x' || t === '1'
    || t === 'true' || t === 'verdadeiro';
};

// A linha "↑ Linhas de exemplo preenchidas 100%, apague-as antes de enviar"
// do modelo. É nota de rodapé, não dado.
const isLinhaNota = (linha: PlanilhaLinha): boolean =>
  /^[↑↓*→]/.test(linha.nome.trim());

export const validarPlanilha = (
  linhas: PlanilhaLinha[],
  opts: ValidarPlanilhaOptions,
): PlanilhaValidada => {
  const erros: PlanilhaErro[] = [];
  const vidas: VidaImportada[] = [];
  let ignoradas = 0;

  const cpfsExistentes = new Set((opts.cpfsExistentes || []).map(onlyDigits));
  const emailsExistentes = new Set((opts.emailsExistentes || []).map(e => String(e).trim().toLowerCase()));

  // Titulares disponíveis: os que já estão cadastrados na empresa, mais os
  // que forem validados nesta mesma planilha (preenchidos na 1ª passada).
  const titulares = new Set(
    (opts.cpfsColaboradoresExistentes || []).map(onlyDigits).filter(Boolean),
  );

  // Onde cada CPF/e-mail apareceu pela primeira vez, para a mensagem
  // apontar a outra linha em vez de só dizer "repetido".
  const cpfVisto = new Map<string, PlanilhaLinha>();
  const emailVisto = new Map<string, PlanilhaLinha>();
  const ondeVi = (l: PlanilhaLinha | undefined): string =>
    l ? (l.aba ? `${l.aba}, linha ${l.numero}` : `linha ${l.numero}`) : 'outra linha';

  // O dropdown da planilha modelo oferece "Blue Protect", "UltraBlue",
  // "Blue Light" e "Alicerce" — e no catálogo o último se chama "Plano
  // Alicerce". Casar só pelo nome exato recusava a planilha inteira por causa
  // de uma palavra que o próprio modelo não escreve. Duas voltas porque o nome
  // cheio tem que vencer o apelido: se um dia existir um plano chamado
  // "Alicerce", ele fica com a chave, não o apelido de "Plano Alicerce".
  const catalogo = new Map<string, PlanoDoCatalogo>();
  opts.planos.forEach(p => catalogo.set(chavePlano(p.name), p));
  opts.planos.forEach((p) => {
    const apelido = chavePlano(p.name, true);
    if (!catalogo.has(apelido)) catalogo.set(apelido, p);
  });

  const validarLinha = (linha: PlanilhaLinha) => {
    const dependente = ehDependente(linha.tipo);
    const cpf = onlyDigits(linha.cpf);
    const email = String(linha.email ?? '').trim().toLowerCase();

    if (isLinhaVazia(linha) || isLinhaNota(linha) || isLinhaExemplo(cpf, email)) {
      ignoradas += 1;
      return;
    }

    // Tipo final do dependente. A planilha (e o parser) só distingue titular
    // de dependente — quem decide Vida+ x só adicional é o núcleo, pelo texto
    // da célula de Plano: "Nenhum Plano contratado" (ou célula vazia) vira só
    // adicional. A tela que cadastra à mão já manda `so_adicional` pronto.
    // Colaborador sem plano continua sendo erro.
    const planoRaw = String(linha.plano ?? '').trim();
    const odontoMarcado = marcadoNaPlanilha(linha.odonto);
    const psicologoMarcado = marcadoNaPlanilha(linha.psicologo);
    const tipoFinal: VidaTipo = dependente
      ? ((linha.tipo === 'so_adicional' || !planoRaw || ehSemPlano(planoRaw)) ? 'so_adicional' : 'vida_mais')
      : 'colaborador';

    const errosDaLinha: PlanilhaErro[] = [];
    const erro = (motivo: string, campo?: keyof PlanilhaLinha) =>
      errosDaLinha.push({ linha: linha.numero, aba: linha.aba, campo, motivo });

    // Nome
    const nome = String(linha.nome ?? '').trim().replace(/\s+/g, ' ');
    if (!nome) erro('nome em branco', 'nome');
    else if (nome.length < 3) erro('nome muito curto', 'nome');

    // CPF
    if (!cpf) erro('CPF em branco', 'cpf');
    else if (!isValidCpf(cpf)) erro(`CPF inválido (${linha.cpf})`, 'cpf');
    else if (cpfsExistentes.has(cpf)) erro(MOTIVO_CPF_JA_CADASTRADO, 'cpf');
    else if (cpfVisto.has(cpf)) erro(`CPF repetido (${ondeVi(cpfVisto.get(cpf))})`, 'cpf');

    // Data de nascimento
    const nascimento = parseDataNascimento(linha.dataNascimento);
    if (!String(linha.dataNascimento ?? '').trim()) {
      erro('data de nascimento em branco', 'dataNascimento');
    } else if (!nascimento) {
      erro(`data de nascimento inválida (${linha.dataNascimento}) — use dd/mm/aaaa`, 'dataNascimento');
    } else {
      const idade = idadeEm(nascimento, opts.hoje);
      const meses = idadeEmMesesEm(nascimento, opts.hoje);
      // Piso de idade só vale para o titular: o dependente (Vida+ ou só
      // adicional) não escolhe nada sozinho, e a planilha modelo traz
      // criança nas próprias linhas de exemplo. O teto é da operadora e
      // vale para qualquer vida.
      const minimoMeses = dependente ? 0 : IDADE_MINIMA_MESES;
      if (meses < 0) erro('data de nascimento no futuro', 'dataNascimento');
      else if (idade >= IDADE_IMPLAUSIVEL) erro('data de nascimento implausível', 'dataNascimento');
      else if (meses > IDADE_MAXIMA_MESES) {
        erro(`idade de ${idadePorExtenso(meses)} — o plano aceita até ${idadePorExtenso(IDADE_MAXIMA_MESES)}`, 'dataNascimento');
      } else if (meses < minimoMeses) {
        erro(`idade abaixo de ${IDADE_MINIMA} anos`, 'dataNascimento');
      }
    }

    // E-mail — só adicional pode repetir o e-mail de qualquer pessoa da
    // empresa e pode ficar em branco: ele não entra na plataforma, não
    // precisa de conta própria. O filho costuma vir com o e-mail da mãe.
    if (tipoFinal === 'so_adicional') {
      if (email && !isValidEmail(email)) erro(`e-mail inválido (${linha.email})`, 'email');
    } else if (!email) {
      erro('e-mail em branco', 'email');
    } else if (!isValidEmail(email)) {
      erro(`e-mail inválido (${linha.email})`, 'email');
    } else if (emailsExistentes.has(email)) {
      erro('e-mail já cadastrado nesta empresa', 'email');
    } else if (emailVisto.has(email)) {
      erro(`e-mail repetido (${ondeVi(emailVisto.get(email))})`, 'email');
    }

    // Plano — só adicional não tem plano de catálogo, mas só existe com pelo
    // menos um adicional marcado; sem isso a linha não é vida nenhuma.
    const plano = tipoFinal === 'so_adicional'
      ? undefined
      : catalogo.get(chavePlano(planoRaw)) || catalogo.get(chavePlano(planoRaw, true));
    if (tipoFinal === 'so_adicional') {
      if (!odontoMarcado && !psicologoMarcado) {
        erro('dependente sem Vida+ e sem adicional — marque Odontológico ou Psicólogo, ou escolha o Vida+', 'plano');
      }
    } else if (!planoRaw) erro('plano em branco', 'plano');
    else if (!plano) erro(`plano "${planoRaw}" não existe no catálogo`, 'plano');
    else if (!dependente && ehPlanoVidaMais(plano.name)) erro('Vida+ é plano de dependente — vai na aba Dependentes', 'plano');

    // Nome da mãe — o Plano Odontológico Clin exige, para qualquer pessoa
    // marcada (titular, Vida+ ou só adicional). Sem odonto, a célula é
    // ignorada: dado pessoal que ninguém pediu não se guarda.
    const nomeMae = String(linha.nomeMae ?? '').trim().replace(/\s+/g, ' ');
    if (odontoMarcado) {
      if (!nomeMae) erro('nome da mãe em branco — obrigatório com Plano Odontológico Clin', 'nomeMae');
      else if (nomeMae.length < 3) erro('nome da mãe muito curto', 'nomeMae');
      else if (nomeMae.length > 150) erro('nome da mãe longo demais (máximo 150 caracteres)', 'nomeMae');
    }

    // Colaborador vinculado — todo dependente tem, e sem ele não existe: é
    // dele que sai o titular_id da vida.
    const titularCpf = onlyDigits(linha.titularCpf);
    if (dependente) {
      if (!titularCpf) erro('dependente sem o CPF do colaborador vinculado', 'titularCpf');
      else if (!isValidCpf(titularCpf)) erro(`CPF do colaborador vinculado inválido (${linha.titularCpf})`, 'titularCpf');
      else if (titularCpf === cpf) erro('o colaborador vinculado não pode ser a própria vida', 'titularCpf');
      else if (!titulares.has(titularCpf)) {
        erro(
          `colaborador vinculado (${formatCpf(titularCpf)}) não está na aba Colaborador `
          + 'nem cadastrado nesta empresa',
          'titularCpf',
        );
      }
    }

    if (errosDaLinha.length) {
      erros.push(...errosDaLinha);
      return;
    }

    cpfVisto.set(cpf, linha);
    // Só adicional fica fora do controle de e-mail repetido: ele não ocupa
    // a vaga de ninguém, então não pode nem bloquear nem ser bloqueado pelo
    // e-mail de outra linha.
    if (tipoFinal !== 'so_adicional') emailVisto.set(email, linha);
    if (!dependente) titulares.add(cpf);

    vidas.push({
      linha: linha.numero,
      aba: linha.aba,
      tipo: tipoFinal,
      nome,
      cpf,
      dataNascimento: nascimento as string,
      email,
      telefone: String(linha.telefone ?? '').trim(),
      planoId: plano ? plano.id : null,
      // Só adicional grava sempre o texto de "sem plano": um dependente que
      // era Vida+ e virou só adicional não pode ficar com "Plano VIDA+" no dado.
      planoNomeRaw: tipoFinal === 'so_adicional'
        ? (planoRaw && ehSemPlano(planoRaw) ? planoRaw : PLANO_SEM_PLANO_TEXTO)
        : planoRaw,
      titularCpf: dependente ? titularCpf : null,
      // Cada pessoa marca a própria — Vida+ e só adicional têm odonto e
      // psicólogo independentes do titular, não mais forçados a false.
      odonto: odontoMarcado,
      psicologo: psicologoMarcado,
      nomeMae: odontoMarcado ? nomeMae : null,
    });
  };

  // Duas passadas, e nesta ordem de propósito: o Vida+ da aba 2 se pendura
  // num colaborador da aba 1, que pode estar em qualquer linha dela. Validar
  // na ordem do arquivo faria o dependente da linha 13 não achar o titular
  // da linha 200. A ordem também é a que a importação precisa — titular
  // gravado antes do dependente, senão não há id a que apontar.
  linhas.filter(l => !ehDependente(l.tipo)).forEach(validarLinha);
  linhas.filter(l => ehDependente(l.tipo)).forEach(validarLinha);

  return {
    vidas,
    erros,
    totalLinhas: linhas.length - ignoradas,
    totalValidas: vidas.length,
    totalErros: erros.length,
    ignoradas,
  };
};

// ── Rascunho de vidas ─────────────────────────────────────────────
// Linha da planilha que não virou vida: tinha erro e ninguém mexeu na prévia,
// ou alguém a mandou para depois de propósito. Fica em cs.vidas_rascunhos,
// FORA de cs.vidas — e é isso que garante que rascunho não conta vida, não vai
// ao Hub, não entra no boleto e não libera acesso.
//
// Máquina de estados: pendente → resolvido | descartado (os dois são finais).
// Pendente se edita e se salva mesmo com erro; só vira vida sem erro nenhum.

export type VidaRascunhoStatus = 'pendente' | 'resolvido' | 'descartado';

export interface LinhaParaRascunho {
  linha: PlanilhaLinha;
  erros: PlanilhaErro[];
}

/** Vazia, nota de rodapé ou exemplo do modelo — `validarPlanilha` as ignora,
 *  e o lote também: não viram vida, rascunho nem descarte. */
const ehLinhaIgnorada = (l: PlanilhaLinha): boolean =>
  isLinhaVazia(l) || isLinhaNota(l)
  || isLinhaExemplo(onlyDigits(l.cpf), String(l.email ?? '').trim().toLowerCase());

/** Motivo de quem já é vida ativa da empresa. É o reenvio da planilha
 *  acumulada (os 400 de antes + 10 novos): essa linha não é pendência. */
export const MOTIVO_CPF_JA_CADASTRADO = 'CPF já cadastrado nesta empresa';

const jaCadastrada = (erros: PlanilhaErro[]): boolean =>
  erros.some(e => e.campo === 'cpf' && e.motivo === MOTIVO_CPF_JA_CADASTRADO);

/**
 * O que a confirmação da prévia grava.
 *
 * Linha cujo CPF já é vida ativa da empresa NÃO vira rascunho: nunca
 * resolveria (o CPF já existe) e um reenvio da planilha acumulada encheria a
 * empresa de rascunhos falsos. Ela só entra na contagem `jaCadastradas`.
 *
 * `linhas` são as que seguiram na prévia (sem rascunho nem descarte) e
 * `validacao` é a validação delas. Linha com erro em `linhas` vira rascunho
 * com os próprios erros (D1): ninguém mexeu nela, e perdê-la obrigaria o RH a
 * mandar a planilha de novo.
 *
 * Mesma linha em rascunho e descarte: o descarte vence — é a escolha que guarda
 * menos dado pessoal, e a tela não deveria deixar as duas coisas ao mesmo tempo.
 */
export const montarLoteDaImportacao = (p: {
  validacao: PlanilhaValidada;
  linhas: PlanilhaLinha[];
  rascunhos: LinhaParaRascunho[];
  descartadas: PlanilhaLinha[];
}): { vidas: VidaImportada[]; rascunhos: LinhaParaRascunho[]; totalDescartadas: number; jaCadastradas: number } => {
  const descartadas = new Set(
    p.descartadas.filter(l => !ehLinhaIgnorada(l)).map(chaveDaLinha),
  );

  const errosPorLinha = new Map<string, PlanilhaErro[]>();
  p.validacao.erros.forEach((e) => {
    const k = chaveDoErro(e);
    errosPorLinha.set(k, [...(errosPorLinha.get(k) || []), e]);
  });

  const rascunhos: LinhaParaRascunho[] = [];
  const emRascunho = new Set<string>();
  const adicionar = (r: LinhaParaRascunho) => {
    const k = chaveDaLinha(r.linha);
    if (descartadas.has(k) || emRascunho.has(k) || ehLinhaIgnorada(r.linha)) return;
    emRascunho.add(k);
    // Rascunho guarda a linha crua: sem odonto, o nome da mãe não vai junto.
    const linha = marcadoNaPlanilha(r.linha.odonto) ? r.linha : { ...r.linha, nomeMae: '' };
    rascunhos.push({ linha, erros: r.erros || [] });
  };

  let jaCadastradas = 0;
  p.rascunhos.forEach(adicionar);
  p.linhas.forEach((l) => {
    const erros = errosPorLinha.get(chaveDaLinha(l));
    if (!erros || !erros.length || descartadas.has(chaveDaLinha(l))) return;
    if (jaCadastrada(erros)) { jaCadastradas += 1; return; }
    adicionar({ linha: l, erros });
  });

  const fora = new Set([...Array.from(descartadas), ...Array.from(emRascunho)]);
  const vidas = p.validacao.vidas.filter(v => !fora.has(chaveDaLinha({ aba: v.aba, numero: v.linha })));

  return { vidas, rascunhos, totalDescartadas: descartadas.size, jaCadastradas };
};

/**
 * Linhas (por `chaveDoErro`) que têm erro em `depois` e não tinham em `antes`.
 * É o que a confirmação usa para saber se a empresa mudou desde a leitura:
 * comparar só a contagem deixava passar um erro novo que coincidisse com um
 * erro corrigido, e travava por erro que já existia.
 */
export const linhasComErroNovo = (antes: PlanilhaValidada, depois: PlanilhaValidada): string[] => {
  const ja = new Set(antes.erros.map(chaveDoErro));
  return Array.from(new Set(depois.erros.map(chaveDoErro))).filter(k => !ja.has(k));
};

/** Os dependentes que se penduram no colaborador desta linha — vão junto para
 *  o rascunho (ou o descarte), porque sem o titular eles não validam. */
export const dependentesDaLinha = (linhas: PlanilhaLinha[], chaveTitular: string): PlanilhaLinha[] => {
  const titular = linhas.find(l => chaveDaLinha(l) === chaveTitular);
  if (!titular || ehDependente(titular.tipo)) return [];
  const cpf = onlyDigits(titular.cpf);
  if (!cpf) return [];
  return linhas.filter(l => ehDependente(l.tipo) && onlyDigits(l.titularCpf) === cpf);
};

/**
 * Valida a linha de um rascunho sozinha, contra o que a empresa já tem.
 *
 * O dependente só resolve se o titular for colaborador ATIVO da empresa
 * (`opts.cpfsColaboradoresExistentes`). Se o titular ainda estiver em
 * rascunho pendente (`titularesEmRascunho`), o erro aponta para ele — é ele
 * que precisa ser resolvido primeiro.
 */
export const validarLinhaDoRascunho = (
  linha: PlanilhaLinha,
  opts: ValidarPlanilhaOptions,
  titularesEmRascunho: PlanilhaLinha[] = [],
): { vida: VidaImportada | null; erros: PlanilhaErro[] } => {
  const r = validarPlanilha([linha], opts);
  if (r.ignoradas) {
    return {
      vida: null,
      erros: [{ linha: linha.numero, aba: linha.aba, motivo: 'linha vazia ou de exemplo — preencha os dados da vida ou descarte' }],
    };
  }

  const cpf = onlyDigits(linha.cpf);
  const titularCpf = onlyDigits(linha.titularCpf);
  const colaboradores = new Set((opts.cpfsColaboradoresExistentes || []).map(onlyDigits));
  const semTitular = ehDependente(linha.tipo) && isValidCpf(titularCpf)
    && titularCpf !== cpf && !colaboradores.has(titularCpf);

  if (!semTitular) return { vida: r.vidas[0] || null, erros: r.erros };

  const pendente = titularesEmRascunho.find(l => !ehDependente(l.tipo) && onlyDigits(l.cpf) === titularCpf);
  const motivo = pendente
    ? `Resolva antes o rascunho do colaborador titular (${enderecoDoErro({ linha: pendente.numero, aba: pendente.aba, motivo: '' })})`
    : 'Colaborador titular não cadastrado: corrija o CPF do titular ou descarte';
  return {
    vida: null,
    erros: r.erros.map(e => (e.campo === 'titularCpf' ? { ...e, motivo } : e)),
  };
};

const plural = (n: number, um: string, varios: string): string => `${n} ${n === 1 ? um : varios}`;

/**
 * "12 importadas · 3 rascunhos (1 pendente · 2 resolvidos) · 2 descartadas".
 * Os três números principais aparecem sempre (zero também diz algo no
 * histórico); o detalhe dos rascunhos só quando vem e há rascunho, sem os zeros.
 */
export const resumoDaImportacao = (i: {
  importadas: number; rascunhos: number; descartadas: number;
  pendentes?: number; resolvidos?: number; rascunhosDescartados?: number;
}): string => {
  const detalhe = [
    i.pendentes ? plural(i.pendentes, 'pendente', 'pendentes') : '',
    i.resolvidos ? plural(i.resolvidos, 'resolvido', 'resolvidos') : '',
    i.rascunhosDescartados ? plural(i.rascunhosDescartados, 'descartado', 'descartados') : '',
  ].filter(Boolean).join(' · ');

  const rascunhos = plural(i.rascunhos, 'rascunho', 'rascunhos')
    + (i.rascunhos > 0 && detalhe ? ` (${detalhe})` : '');

  return [
    plural(i.importadas, 'importada', 'importadas'),
    rascunhos,
    plural(i.descartadas, 'descartada', 'descartadas'),
  ].join(' · ');
};

// ── Confirmação do boleto ─────────────────────────────────────────

/** Abaixo disto o Hub BlueHub responde 200 e NÃO cobra (`skipped_below_min`). */
export const PISO_VIDAS_BOLETO_HUB = 3;

const reais = (cents: number): string =>
  (cents / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });

/**
 * O texto que a tela mostra antes de disparar o boleto. As vidas enviadas
 * são soberanas: o valor é o delas. A negociação inicial aparece só quando
 * diverge, para ninguém ser pego de surpresa — nunca para corrigir o valor.
 * `bloqueio` impede o disparo; `avisos` só informam.
 */
export const confirmacaoDoBoleto = (p: {
  vidas: number; valorCents: number; vidasContratadas: number; valorContratoCents: number;
  /** Linhas da planilha em rascunho pendente. Não estão em cs.vidas, então não
   *  entram no valor — o aviso existe para ninguém esquecê-las. Nunca bloqueia (D3). */
  rascunhosPendentes?: number;
}): { mensagem: string; avisos: string[]; bloqueio: string | null } => {
  const pendentes = Math.max(0, Math.floor(Number(p.rascunhosPendentes) || 0));
  const avisoRascunhos = pendentes > 0
    ? [`Há ${pendentes} linha(s) da planilha em rascunho, fora deste boleto. Resolva antes se devem ser cobradas neste mês.`]
    : [];
  if (!p.valorCents || !p.vidas) {
    return {
      mensagem: '',
      avisos: avisoRascunhos,
      bloqueio: 'Nenhuma vida cadastrada para cobrar. Importe as vidas antes de gerar o boleto — é por elas que o valor sai.',
    };
  }
  const avisos: string[] = [];
  if (p.valorContratoCents && p.valorContratoCents !== p.valorCents) {
    avisos.push(
      `A negociação inicial era ${p.vidasContratadas} vida(s) por ${reais(p.valorContratoCents)}. `
      + 'O boleto segue as vidas cadastradas.',
    );
  }
  if (p.vidas < PISO_VIDAS_BOLETO_HUB) {
    avisos.push(
      `Com ${p.vidas} vida(s) a empresa fica abaixo do mínimo de ${PISO_VIDAS_BOLETO_HUB} do BlueHub: `
      + 'o Hub não emite o boleto até completar o mínimo.',
    );
  }
  avisos.push(...avisoRascunhos);
  return {
    mensagem: `O boleto vai cobrar ${reais(p.valorCents)} por ${p.vidas} vida(s) cadastrada(s).`,
    avisos,
    bloqueio: null,
  };
};

/** Vidas contratadas na venda = soma das linhas de plano (adicional não
 *  conta como vida). Serve para avisar quando a planilha não bate com o
 *  que foi vendido — aviso, nunca bloqueio: a doc do hub descreve o caso
 *  de a empresa pagar por N licenças e o RH preencher ao longo da semana. */
export const vidasContratadas = (linhas: ProposalPlanLine[] | undefined): number =>
  (linhas || [])
    .filter(l => l && l.kind !== 'addon')
    .reduce((soma, l) => soma + Math.max(0, Math.floor(l.quantity || 0)), 0);

/** Vidas não entram na conta: a empresa pode ser liberada sem nenhuma e o RH
 *  cadastrá-las depois. É o que a fila acompanha em "Aguardando planilha" e
 *  "Vidas incompletas" — exigir aqui deixaria essas situações sempre vazias. */
export const podeLiberarImplantacao = (
  contratosConcluidos: boolean,
  statusPagamento: string,
): boolean => contratosConcluidos && statusPagamento !== 'pendente_restante';

// ── Fila de implantação ───────────────────────────────────────────
// A empresa entra na fila quando o comercial a libera, o que pode acontecer
// antes de a formalização terminar. A situação abaixo é o que falta para a
// implantação começar — derivada, nunca gravada: qualquer edição no
// contrato ou na planilha já a recalcula.

export type SituacaoImplantacao =
  | 'cancelada'
  | 'sem_contrato'
  | 'contrato_pendente'
  | 'sem_planilha'
  | 'vidas_incompletas'
  | 'pronta';

export const SITUACAO_IMPLANTACAO_LABELS: Record<SituacaoImplantacao, string> = {
  cancelada:         'Contrato cancelado',
  sem_contrato:      'Sem formalização',
  contrato_pendente: 'Contrato em andamento',
  sem_planilha:      'Aguardando planilha',
  vidas_incompletas: 'Vidas incompletas',
  pronta:            'Pronta para implantar',
};

export const situacaoImplantacao = (cliente: {
  contratoStatus: ContratoStatus | null;
  vidasContratadas: number;
  vidasCadastradas: number;
  ultimaImportacaoStatus: ImportacaoStatus | null;
}): SituacaoImplantacao => {
  const { contratoStatus, vidasContratadas: esperadas, vidasCadastradas: cadastradas } = cliente;

  if (contratoStatus === 'cancelado') return 'cancelada';
  if (!contratoStatus) return 'sem_contrato';
  // `assinado` e `ativo` são os dois finais: o resto ainda está em trânsito.
  if (contratoStatus !== 'assinado' && contratoStatus !== 'ativo') return 'contrato_pendente';
  // Vida cadastrada à mão também conta — nem toda empresa manda planilha.
  if (!cliente.ultimaImportacaoStatus && cadastradas === 0) return 'sem_planilha';
  // Sem vidas contratadas não há contra o que comparar: a venda pode ter
  // sido por licenças em aberto. Aí o que existe já basta.
  if (esperadas > 0 && cadastradas < esperadas) return 'vidas_incompletas';
  return 'pronta';
};

// ── Gatilhos ──────────────────────────────────────────────────────

export const GATILHO_LABELS: Record<GatilhoTipo, string> = {
  'contrato.gerar':      'Gerar contrato',
  'vidas.enviar':        'Enviar vidas ao hub',
  'boleto.gerar':        'Gerar boleto',
  'implantacao.liberar': 'Liberar implantação',
  'acesso.enviar':       'Enviar acesso',
  'acesso.revogar':      'Revogar acesso',
  'boas_vindas.enviar':  'Enviar boas-vindas',
};

/** Chave de idempotência por tipo. Espelha o índice único parcial
 *  `ux_cs_gatilhos_dedupe (empresa_id, tipo, dedupe_key) WHERE sucesso`. */
export const dedupeKeyDe = (
  tipo: GatilhoTipo,
  alvo: { contratoId?: string | null; documentoTipo?: DocumentoTipo;
          competenciaId?: string | null; importacaoId?: string | null;
          vidaId?: string | null; empresaId?: string; dia?: string },
): string => {
  switch (tipo) {
    case 'contrato.gerar':
      return `${alvo.contratoId || ''}:${alvo.documentoTipo || 'blue'}`;
    case 'vidas.enviar':
      return String(alvo.importacaoId || '');
    case 'boleto.gerar':
      return String(alvo.competenciaId || '');
    case 'acesso.revogar':
      return String(alvo.vidaId || '');
    case 'acesso.enviar':
      // Reenvio de acesso é legítimo mais de uma vez; o que não pode é
      // duas vezes no mesmo dia por engano. Daí a data na chave.
      return `${alvo.vidaId || alvo.empresaId || ''}:${alvo.dia || ''}`;
    default:
      return '';
  }
};

export const GATILHO_STATUS_LABELS: Record<GatilhoStatus, string> = {
  pendente:  'Pendente',
  enviando:  'Enviando',
  sucesso:   'Sucesso',
  falha:     'Falha',
  cancelado: 'Cancelado',
};

// ── Ficha do cliente (cs.obter_ficha_cliente) ─────────────────────
// A mesma rota `/clientes/:empresaId` serve dois públicos: o CS (ficha
// COMPLETA — contrato, vidas, financeiro, automações) e o time de
// Implantação (ficha REDUZIDA — a mesma tela de sempre, de
// `obter_cliente_implantacao`). Quem decide qual é o banco, nunca a rota.

/** Uma linha de `cs.obter_ficha_cliente`. No nível reduzido só
 *  `empresa_id` vem preenchido — o resto da tela reduzida é
 *  responsabilidade da RPC antiga, chamada à parte pela própria tela. */
export interface FichaClienteRow {
  nivel: 'completo' | 'reduzido';
  empresa_id: string;
  workspace_id: string | null;
  nome: string | null;
  lead_id: string | null;
  sale_id: string | null;
}

export type EstadoFicha =
  | { estado: 'completo'; empresaId: string; workspaceId: string; nome: string; leadId: string | null; saleId: string | null }
  | { estado: 'reduzido'; empresaId: string }
  | { estado: 'nao_encontrado' }
  | { estado: 'sem_acesso' };

/**
 * Traduz o resultado crú da RPC para o estado da página — pura de
 * propósito, para o teste cobrir as quatro saídas sem banco.
 *
 * Zero linhas é empresa inexistente; `42501` é fora dos dois grupos (CS ou
 * Implantação); o `nivel` da única linha restante escolhe entre completo e
 * reduzido. Qualquer OUTRO erro (rede, 500) não é um dos quatro estados —
 * continua subindo pela queryFn, e quem mostra é o `.error` do React Query.
 */
export const estadoDaFicha = (
  rows: FichaClienteRow[] | null | undefined,
  erroCode?: string | null,
): EstadoFicha => {
  if (erroCode === '42501') return { estado: 'sem_acesso' };

  const row = (rows || [])[0];
  if (!row) return { estado: 'nao_encontrado' };
  if (row.nivel === 'reduzido') return { estado: 'reduzido', empresaId: row.empresa_id };
  // Nível que a tela não conhece nunca abre a ficha completa por omissão.
  if (row.nivel !== 'completo') return { estado: 'sem_acesso' };

  return {
    estado: 'completo',
    empresaId: row.empresa_id,
    workspaceId: row.workspace_id || '',
    nome: row.nome || '',
    leadId: row.lead_id ?? null,
    saleId: row.sale_id ?? null,
  };
};
